import React, { useState, useCallback, memo, lazy } from 'react'
import { useIsClient, useIsGuest, useIsRealAdmin } from 'hooks/user'
import { useRouter } from 'next/router'
import { isProduction } from 'services/environment'
import ResponsiveButton from 'components/ResponsiveButton'
import MasqButton from 'components/MasqButton'
import NotifyAboutJob from 'components/NotifyAboutJob'
import Suspense from 'components/Suspense'
import ChatButton from 'components/ChatButton'
import Button from 'components/Button'
import ButtonStack from 'components/ButtonStack'
import { goToSignup } from 'services/router'
import { Send } from '@mui/icons-material'
import { PreloadedQuery, usePreloadedQuery } from 'react-relay'
import { graphql } from 'relay-runtime'
import { ProfileActions_Query } from '__generated__/ProfileActions_Query.graphql'
import { useFragment } from 'react-relay/hooks'
import { ProfileActions_Freelancer$key } from '__generated__/ProfileActions_Freelancer.graphql'
import { ProfileActions_User$key } from '__generated__/ProfileActions_User.graphql'
import useStyles from '../styles'

export const ProfileActionsQuery = graphql`
  query ProfileActions_Query($userId: ID!) {
    currentUser {
      ...ProfileActions_User
    }

    freelancer: node(id: $userId) {
      ... on User {
        ...ProfileActions_Freelancer
      }
    }
  }
`

// Import some components dynamically because they are not needed immediately
const HirePipelineActions = lazy(
  () => import(/* webpackChunkName: "HirePipelineActions" */'./HirePipelineActions'),
)

interface IProfileActionsProps {
  isInSignupFlow: boolean
  preloadedQuery: PreloadedQuery<ProfileActions_Query>
  jobId?: string
}

const ProfileActions = (props: IProfileActionsProps) => {
  const { classes } = useStyles()
  const {
    isInSignupFlow,
    preloadedQuery,
    jobId,
  } = props

  const data = usePreloadedQuery(ProfileActionsQuery, preloadedQuery)

  const freelancer = useFragment<ProfileActions_Freelancer$key>(graphql`
    fragment ProfileActions_Freelancer on User {
      id
      canNotify
      canMakeOffer
      isChatContact

      directChatThread {
        id
      }

      ...NotifyAboutJob_User
      ...MasqButton_User
      ...ChatButton_Recipient
    }
  `, data?.freelancer)

  const user = useFragment<ProfileActions_User$key>(graphql`
    fragment ProfileActions_User on User {
      ...user_useIsGuest
      ...user_useIsRealAdmin
      ...user_useIsClient
    }
  `, data?.currentUser)

  const router = useRouter()
  const isGuest = useIsGuest(user)
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false)
  const isAdmin = useIsRealAdmin(user)
  const isClient = useIsClient(user)

  const redirectClient = useCallback(() => goToSignup(router, 'company', true), [router])

  const makeOffer = useCallback(() => {
    if (isClient) {
      router.push(`/make_offer?member=${freelancer?.id}`)
    } else if (isGuest) {
      redirectClient()
    }
  }, [isGuest, isClient, isGuest])

  const openNotifyDialog = useCallback(() => {
    if (isClient) {
      setNotifyDialogOpen(true)
    } else if (isGuest) {
      redirectClient()
    }
  }, [isGuest, isClient])

  const canNotify = isGuest || freelancer?.canNotify
  const canMakeOffer = isGuest || freelancer?.canMakeOffer
  const showChatButton = Boolean(freelancer?.directChatThread?.id || freelancer?.isChatContact || isAdmin)
  const showHirePipelineActions = Boolean(jobId && freelancer?.id)

  if (!isGuest && !isClient) {
    if (isAdmin) {
      return (
        <div className={classes.actions}>
          <MasqButton userFragmentRef={freelancer ?? null} />
        </div>
      )
    }

    return null
  }

  if (isInSignupFlow) {
    return (
      <div className={classes.actions}>
        <Button color="primary" onClick={() => router.back()}>
          Continue
        </Button>
      </div>
    )
  }

  if (!canNotify && !canMakeOffer && !showHirePipelineActions && isProduction()) return null

  return (
    <div className={classes.actions}>
      {!showHirePipelineActions && (
        <ButtonStack>
          {canNotify && (
            <ResponsiveButton
              icon={<Send />}
              label="Share Opportunity"
              mobileLabel="Notify"
              onClick={openNotifyDialog}
              data-cy="notify-freelancer"
            />
          )}

          {canMakeOffer && (
            <ResponsiveButton
              color="primary"
              label="Hire Me"
              mobileLabel="Hire Me"
              onClick={makeOffer}
              data-cy="make-offer"
              data-testid="make-offer"
            />
          )}

          {showChatButton && (
            <ChatButton recipientFragmentRef={freelancer ?? null} />
          )}

          <MasqButton userFragmentRef={freelancer ?? null} />
        </ButtonStack>
      )}

      {showHirePipelineActions && freelancer?.id && jobId && (
        <Suspense>
          <HirePipelineActions
            freelancerId={freelancer.id}
            jobId={jobId}
          />
        </Suspense>
      )}

      <Suspense>
        <NotifyAboutJob
          open={notifyDialogOpen}
          onClose={() => setNotifyDialogOpen(false)}
          userFragmentRef={freelancer}
        />
      </Suspense>
    </div>
  )
}

export default memo(ProfileActions)
